import { annotateTimeline, humanMonths } from "@/lib/timeline";
import { AlertTriangle } from "lucide-react";

function MissingPill({ label = "missing" }) {
    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-300 px-1.5 py-0.5 font-bold">
            <AlertTriangle className="w-3 h-3" /> {label}
        </span>
    );
}

function GapChip({ label, reason }) {
    return (
        <div className="my-2 flex items-center gap-2" data-testid="timeline-gap">
            <div className="h-px bg-rose-300 flex-1" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-300 px-2 py-0.5 font-bold">
                Gap · {label}{reason ? ` · ${reason}` : ""}
            </span>
            <div className="h-px bg-rose-300 flex-1" />
        </div>
    );
}

function fmt(dateOrPresent) {
    if (!dateOrPresent) return null;
    if (dateOrPresent === "present") return "Present";
    try {
        return dateOrPresent.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch { return String(dateOrPresent); }
}

/**
 * Renders an array of experience/education items with month-precision gap chips
 * between consecutive entries (most-recent-first ordering).
 *
 * type: "experience" | "education"
 */
export default function TimelineList({ items, type = "experience" }) {
    const annotated = annotateTimeline(items || []);
    if (annotated.length === 0) {
        return (
            <div className="text-sm text-foreground/80 border border-dashed border-border p-4">
                <MissingPill label={`No ${type} found in resume`} />
            </div>
        );
    }
    const isExp = type === "experience";

    return (
        <ol className="space-y-2" data-testid={`timeline-${type}`}>
            {annotated.map((it, idx) => {
                const title = isExp ? (it.title || it.role) : (it.degree || it.title);
                const where = isExp ? (it.company) : (it.institution || it.school || it.university);
                const showStart = fmt(it._start);
                const showEnd = fmt(it._end);
                const startMissing = it._startMissing;
                const endMissing = it._endMissing && !it._end;

                return (
                    <li key={idx}>
                        {idx === 0 && it._gapAfter && (
                            <GapChip label={it._gapAfter.label} reason={it._gapAfter.reason} />
                        )}
                        <div className="border border-border bg-white p-3" data-testid={`timeline-item-${type}-${idx}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm leading-snug">
                                        {title ? title : <MissingPill label="title missing" />}
                                        {where ? <span className="text-foreground/80"> · {where}</span> : <span className="ml-2"><MissingPill label={isExp ? "company missing" : "institution missing"} /></span>}
                                    </div>
                                    <div className="text-xs font-mono text-foreground/80 mt-1 flex items-center gap-1.5 flex-wrap">
                                        {startMissing ? <MissingPill label="start missing" /> : <span>{showStart || "?"}</span>}
                                        <span>–</span>
                                        {endMissing && !it._end ? <MissingPill label="end missing" /> : <span>{showEnd || "?"}</span>}
                                        {it.location && <span>· {it.location}</span>}
                                    </div>
                                    {it.summary && <div className="text-xs text-foreground/80 mt-2 whitespace-pre-wrap">{it.summary}</div>}
                                </div>
                            </div>
                        </div>
                        {it._gapBefore && idx < annotated.length - 1 && (
                            <GapChip label={it._gapBefore.label} />
                        )}
                    </li>
                );
            })}
        </ol>
    );
}
